/**
 * Precarga del Inicio: a los 0,5 s de montar el Sidebar se traen las estadísticas, así el primer
 * clic en «Inicio» ya las encuentra listas.
 *
 * 🪤 Antes escribía en `dashboardStatsCache` —una caché a mano que el Inicio dejó de leer cuando
 * pasó a TanStack Query (ADR-0012)—. O sea que hacía el RPC y guardaba el resultado donde nadie
 * miraba: un viaje al servidor por sesión, gratis para nadie. Un prefetch desconectado no se nota,
 * no rompe nada y no avisa; sólo cuesta.
 *
 * Ahora siembra la MISMA entrada de caché que consume la pantalla, usando la clave y el mapeo
 * compartidos de `dashboardQuery`. Si divergen, vuelve el problema — por eso viven en un solo lado.
 */

import type { QueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import {
  claveDashboard,
  mapearRpcDashboard,
  rangoPorDefectoDashboard,
  type EstadisticasPanel,
  type SerieSparkline,
} from '@/lib/dashboardQuery';

/** La forma que espera el Inicio. Las tres listas van vacías: el camino RPC no las usa. */
interface DatosPanelPrecargados {
  stats: EstadisticasPanel;
  sparkline: SerieSparkline | null;
  orders: never[];
  products: never[];
  customers: never[];
}

export async function prefetchDashboardStats(qc: QueryClient, tenantId: string): Promise<void> {
  const { start, end } = rangoPorDefectoDashboard();
  const canal = 'all';

  // `prefetchQuery` y no `fetchQuery`: si la entrada ya está fresca no hace nada. Sin eso, cada
  // navegación dispararía el RPC de nuevo — exactamente lo que la caché vino a evitar.
  await qc.prefetchQuery({
    queryKey: claveDashboard(tenantId, start, end, canal),
    staleTime: 45_000,
    queryFn: async (): Promise<DatosPanelPrecargados> => {
      const { data, error } = await supabase.rpc('get_dashboard_stats', {
        p_tenant_id: tenantId,
        p_start_date: start.toISOString(),
        p_end_date: end.toISOString(),
        p_channel: canal,
      });
      // 🪤 Si el RPC falla, se LANZA en vez de devolver algo vacío. Sembrar un panel en cero sería
      // peor que no sembrar nada: la pantalla mostraría ventas 0 como si fuera el dato real.
      if (error || !data || typeof data !== 'object') {
        throw error ?? new Error('El RPC del dashboard no devolvió datos');
      }
      const { stats, sparkline } = mapearRpcDashboard(data as Record<string, unknown>);
      return { stats, sparkline, orders: [], products: [], customers: [] };
    },
  });
}
